import { AxiosError } from 'axios'
import { useMutation, UseMutationOptions, useQueryClient } from 'react-query'
import { useDispatch } from 'react-redux'
import { useConfig } from '../../config'
import { setToken } from '../../utils'

import { pushDangerNotification, pushSuccessNotification } from '../snackbar'
import { userApi } from './user.api'
import { USER_KEY } from './user.constants'
import {
  ProfileChangeEmailRequest,
  ProfileChangePasswordRequest,
  ProfileConfirmEmailRequest,
  ProfileConfirmEmailResponse,
  ProfileUser,
  SaveProfileUser
} from './user.types'
import { useRouter } from 'next/router'

export const useMutationLogout = (
  options?: UseMutationOptions<unknown, AxiosError>
) => {
  const queryClient = useQueryClient()
  const { locale } = useRouter()

  const mutation = useMutation(() => userApi.logout(locale ?? 'en'), {
    ...options,
    onSuccess: (data, variables, context) => {
      queryClient.clear()

      options?.onSuccess?.(data, variables, context)
    }
  })

  return mutation
}

export const useMutationSaveProfileUser = (
  options?: UseMutationOptions<ProfileUser, AxiosError, SaveProfileUser>
) => {
  const queryClient = useQueryClient()
  const dispatch = useDispatch()
  const { locale } = useRouter()

  const mutation = useMutation(
    async (requestBody) =>
      await userApi.saveProfile(requestBody, locale ?? 'en'),
    {
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
      },
      onSuccess: (data) => {
        dispatch(pushSuccessNotification('Профиль успешно сохранен'))
        queryClient.setQueryData<ProfileUser>([USER_KEY, locale], data)
      },
      ...options
    }
  )

  return mutation
}

export const useMutationChangeEmailProfileUser = (
  options?: UseMutationOptions<unknown, AxiosError, ProfileChangeEmailRequest>
) => {
  const dispatch = useDispatch()
  const { locale } = useRouter()

  const mutation = useMutation(
    async (requestBody) =>
      await userApi.changeEmail(requestBody, locale ?? 'en'),
    {
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
      },
      onSuccess: () => {
        dispatch(
          pushSuccessNotification(
            'Письмо с подтверждением изменения email отправлено на почту'
          )
        )
      },
      ...options
    }
  )

  return mutation
}

export const useMutationChangePasswordProfileUser = (
  options?: UseMutationOptions<
    unknown,
    AxiosError,
    ProfileChangePasswordRequest
  >
) => {
  const dispatch = useDispatch()
  const { locale } = useRouter()

  const mutation = useMutation(
    async (requestBody) =>
      await userApi.changePassword(requestBody, locale ?? 'en'),
    {
      onError: (error) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
      },
      onSuccess: () => {
        dispatch(pushSuccessNotification('Пароль успешно изменен'))
      },
      ...options
    }
  )

  return mutation
}

export const useMutationConfirmEmailProfileUser = (
  options?: UseMutationOptions<
    ProfileConfirmEmailResponse,
    AxiosError,
    ProfileConfirmEmailRequest
  >
) => {
  const dispatch = useDispatch()
  const config = useConfig()
  const { locale } = useRouter()

  const mutation = useMutation(
    async (requestBody) =>
      await userApi.confirmChangeEmail(requestBody, locale ?? 'en'),
    {
      ...options,
      onError: (error, variables, context) => {
        dispatch(
          pushDangerNotification(error.response?.data?.message || error.message)
        )
        options?.onError?.(error, variables, context)
      },
      onSuccess: (response, variables, context) => {
        setToken(response.token, response.ttl, config.cookiesDomain)
        dispatch(pushSuccessNotification('Email успешно подтвержден'))
        options?.onSuccess?.(response, variables, context)
      }
    }
  )

  return mutation
}
